import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tag, SkeletonText, InlineNotification } from '@carbon/react';
import {
  Calendar,
  Task,
  Time,
  User,
  Group,
  ChevronDown,
  Close,
} from '@carbon/icons-react';
import { Link } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import Collapsible from './Collapsible';
// Deljeni dizajn sistema zadataka (.tasks-item, .tasks-group, .tasks-status,
// .task-details, side panel...) - identican izgled kao na "Zadaci" stranici.
import '../pages/Tasks.css';
import './EmployeeTasksTab.css';

// ===========================================
// Helper funkcije
// (skromna kopija onih iz Tasks.js - drze isti prikaz kao /tasks stranica)
// ===========================================

// Status zadatka -> naziv na srpskom.
const STATUS_LABELS = {
  waiting: 'Čeka izvršenje',
  in_progress: 'U toku',
  completed: 'Završen',
  canceled: 'Otkazan',
  cancelled: 'Otkazan',
};

const severityLabel = (severity) => {
  if (severity === 1) return 'Veoma Važno';
  if (severity === 2) return 'Važno';
  if (severity === 3) return 'Normalno';
  return 'Važno';
};

const severityClass = (severity) => {
  if (severity === 1) return 'high';
  if (severity === 2) return 'medium';
  if (severity === 3) return 'low';
  return 'medium';
};

// yyyy-MM-dd u lokalnoj vremenskoj zoni (za <input type="date">)
const toLocalDateInput = (date) => {
  const d = date || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
};

// Nazivi tipova zaposlenih koji prate zadatak (employeeTypeNames je poravnat po
// indeksu sa employeeTypeIds). Fallback na legacy jedninu.
const getTaskEmployeeTypeNames = (task) => {
  if (!task) return '-';
  if (Array.isArray(task.employeeTypeNames) && task.employeeTypeNames.length > 0) {
    return task.employeeTypeNames.join(', ');
  }
  return task.employeeTypeName || '-';
};

// Normalizuje telo odgovora u niz (podržava { data: [...] } i slične omote).
const extractData = (res) => {
  const body = res.data;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  if (body && Array.isArray(body.value)) return body.value;
  if (body && Array.isArray(body.$values)) return body.$values;
  if (body && Array.isArray(body.items)) return body.items;
  if (body && body.data && Array.isArray(body.data.$values)) return body.data.$values;
  if (body && body.data && Array.isArray(body.data.items)) return body.data.items;
  return [];
};

// Normalizuje odgovor /api/Task/Employee/by-date u niz statusnih sekcija:
// [{ category, count, categories: [{ category, count, tasks: [...] }] }]
//
// Backend vraća 3 nivoa: status sekcije ("Završeni", "Dodeljeni", "Otkazani"),
// unutar njih kategorije ("Doručak", "Fizička aktivnost"...), a unutar njih
// zadatke. Redosled dolazi sa backenda i ne menja se. Radi robustnosti
// tolerišemo i starije (pliće) formate.
const extractEmployeeTaskSections = (res) => {
  const list = extractData(res);
  if (!Array.isArray(list) || list.length === 0) return [];

  // Novi format: statusne sekcije sa `categories`.
  const hasSections = list.every((item) => item && Array.isArray(item.categories));
  if (hasSections) {
    return list.map((section) => {
      const categories = (section.categories || []).map((cat) => ({
        category: cat?.category || null,
        count:
          typeof cat?.count === 'number'
            ? cat.count
            : Array.isArray(cat?.tasks)
            ? cat.tasks.length
            : 0,
        tasks: Array.isArray(cat?.tasks) ? cat.tasks : [],
      }));
      return {
        category: section.category || null,
        count:
          typeof section.count === 'number'
            ? section.count
            : categories.reduce((sum, c) => sum + c.tasks.length, 0),
        categories,
      };
    });
  }

  // Fallback: lista grupa po kategorijama [{ category, count, tasks }].
  const hasGroups = list.every((item) => item && Array.isArray(item.tasks));
  if (hasGroups) {
    const tasks = list.flatMap((g) => (Array.isArray(g.tasks) ? g.tasks : []));
    return [
      {
        category: null,
        count: tasks.length,
        categories: list.map((g) => ({
          category: g.category || null,
          count:
            typeof g.count === 'number'
              ? g.count
              : Array.isArray(g.tasks)
              ? g.tasks.length
              : 0,
          tasks: Array.isArray(g.tasks) ? g.tasks : [],
        })),
      },
    ];
  }

  // Fallback: ravna lista zadataka.
  return [
    {
      category: null,
      count: list.length,
      categories: [{ category: null, count: list.length, tasks: list }],
    },
  ];
};

// ===========================================
// Red zadatka (bez selekcije - samo pregled)
// ===========================================
const EmployeeTaskRow = React.memo(function EmployeeTaskRow({ task, onOpen }) {
  return (
    <div
      className={`tasks-item tasks-item--severity-${severityClass(task.severity)} ${
        task.status === 'completed' ? 'tasks-item--completed' : ''
      }`}
      onClick={() => onOpen(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(task);
        }
      }}
    >
      <div className="tasks-item__content">
        <span className="tasks-item__title">{task.title}</span>
        <div className="tasks-item__meta">
          {/* Zadaci kreirani sa assignToAll = true se prikazuju kao
              "Dodeljeno svima" (bez imena pojedinačnog zaposlenog). */}
          {task.assignToAll ? (
            <span className="tasks-item__meta-item tasks-item__meta-item--all">
              <Group size={14} />
              Dodeljeno svima
            </span>
          ) : task.assignedToName ? (
            <span className="tasks-item__meta-item">
              <User size={14} />
              {task.assignedToName}
            </span>
          ) : null}
          {task.residentName && (
            <span className="tasks-item__meta-item">
              <User size={14} />
              {task.residentName}
            </span>
          )}
          {task.mealTypeName && (
            <span className="tasks-item__meta-item">
              <Task size={14} />
              {task.mealTypeName}
            </span>
          )}
          {task.scheduledTime && (
            <span className="tasks-item__meta-item">
              <Time size={14} />
              {formatTime(task.scheduledTime)}
            </span>
          )}
        </div>
      </div>
      <span className={`tasks-status tasks-status--${task.status}`}>
        {STATUS_LABELS[task.status] || task.status}
      </span>
    </div>
  );
});

// ===========================================
// EmployeeTasksTab
// Tab "Zadaci" na profilu zaposlenog.
// GET /api/Task/Employee/by-date?employeeId=...&date=...&assigned=0
// ===========================================
const EmployeeTasksTab = ({ employeeId }) => {
  const [selectedDate, setSelectedDate] = useState(() => toLocalDateInput(new Date()));
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Skupuljene sekcije (status) i kategorije - zadaci iz njih se ne prikazuju.
  // Sekcije su podrazumevano razvijene (odmah se vidi pregled po statusu),
  // a kategorije skupuljene (isto kao admin "Zadaci" stranica).
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());

  // Task details side panel
  const [selectedTask, setSelectedTask] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [isNotesExpanded, setIsNotesExpanded] = useState(true);

  useEffect(() => {
    if (!employeeId) return;

    let isActive = true;
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append('employeeId', employeeId);
        params.append('date', selectedDate);
        // 0 = svi zadaci (dodeljeni, završeni, otkazani) za izabrani datum.
        params.append('assigned', '0');
        const response = await axiosInstance.get(
          `/api/Task/Employee/by-date?${params.toString()}`
        );
        if (!isActive) return;
        const parsed = extractEmployeeTaskSections(response);
        setSections(parsed);
        // Sve kategorije su skupuljene pri učitavanju (kao na /tasks).
        setCollapsedCategories(
          new Set(
            parsed.flatMap((section, sIdx) =>
              (section.categories || []).map(
                (cat, cIdx) =>
                  `${section.category || `__section-${sIdx}`}::${cat.category || cIdx}`
              )
            )
          )
        );
      } catch (err) {
        if (!isActive) return;
        console.error('Greška pri učitavanju zadataka zaposlenog:', err);
        setError('Nije moguće učitati zadatke. Pokušajte ponovo.');
        setSections([]);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchTasks();
    return () => {
      isActive = false;
    };
  }, [employeeId, selectedDate]);

  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCategory = (key) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const goToday = () => setSelectedDate(toLocalDateInput(new Date()));

  const fetchNotes = useCallback(async (taskId) => {
    if (!taskId) return;
    setNotesLoading(true);
    setNotesError(null);
    try {
      const response = await axiosInstance.get(`/api/TaskNote/task/${taskId}`);
      const body = response.data;
      const payload = body && body.data ? body.data : body;
      setNotes(Array.isArray(payload) ? payload : []);
    } catch (err) {
      // 404 (nema komentara) nije greška - prikaži prazno stanje.
      if (err?.response?.status === 404) {
        setNotes([]);
      } else {
        console.error('Greška pri učitavanju komentara:', err);
        setNotesError('Greška pri učitavanju komentara');
      }
    } finally {
      setNotesLoading(false);
    }
  }, []);

  const openTaskDetails = useCallback(
    (task) => {
      setSelectedTask(task);
      setIsSidePanelOpen(true);
      setNotes([]);
      setNotesError(null);
      setIsNotesExpanded(true);
      fetchNotes(task?.id);
    },
    [fetchNotes]
  );

  const closePanel = () => {
    setIsSidePanelOpen(false);
    setSelectedTask(null);
    setNotes([]);
    setNotesError(null);
    setIsNotesExpanded(true);
  };

  return (
    <div className="emp-tasks">
      {/* Toolbar (isto kao na /tasks) + "Danas" */}
      <div className="tasks-toolbar">
        <div className="tasks-date-group">
          <div className="tasks-date-picker">
            <Calendar size={18} />
            <input
              type="date"
              className="tasks-date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              aria-label="Izaberite datum"
            />
          </div>
          <button type="button" className="emp-tasks__today-btn" onClick={goToday}>
            Danas
          </button>
        </div>
      </div>

      {/* Greška */}
      {error && (
        <InlineNotification
          kind="error"
          title="Greška"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast
          style={{ marginBottom: '0.5rem' }}
        />
      )}

      {/* Lista zadataka (isto kao na /tasks) */}
      {loading ? (
        <div className="tasks-skeleton">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="tasks-skeleton-item">
              <SkeletonText width="60%" />
              <SkeletonText width="30%" />
            </div>
          ))}
        </div>
      ) : sections.length === 0 ? (
        <div className="tasks-empty">
          <div className="empty-icon">📋</div>
          <p className="empty-title">Nema zadataka</p>
          <p className="empty-subtitle">Nema zadataka za izabrani datum</p>
        </div>
      ) : (
        <div className="tasks-groups">
          {sections.map((section, sIdx) => {
            const sectionKey = section.category || `__section-${sIdx}`;
            const isSectionCollapsed = collapsedSections.has(sectionKey);
            return (
              <div key={sectionKey} className="tasks-group emp-tasks__status-group">
                {section.category && (
                  <button
                    type="button"
                    className={`tasks-group__header ${isSectionCollapsed ? '' : 'is-expanded'}`}
                    onClick={() => toggleSection(sectionKey)}
                    aria-expanded={!isSectionCollapsed}
                  >
                    <span className="tasks-group__title">{section.category}</span>
                    <span className="tasks-group__count">{section.count}</span>
                    <ChevronDown
                      size={16}
                      className={`tasks-group__chevron ${isSectionCollapsed ? '' : 'is-expanded'}`}
                    />
                  </button>
                )}
                <Collapsible open={!isSectionCollapsed}>
                  <div className="tasks-group__items emp-tasks__categories">
                    {(section.categories || []).map((cat, cIdx) => {
                      const catKey = `${sectionKey}::${cat.category || cIdx}`;
                      const isCatCollapsed = collapsedCategories.has(catKey);
                      return (
                        <div key={catKey} className="tasks-group emp-tasks__category-group">
                          {cat.category && (
                            <button
                              type="button"
                              className={`tasks-group__header ${
                                isCatCollapsed ? '' : 'is-expanded'
                              }`}
                              onClick={() => toggleCategory(catKey)}
                              aria-expanded={!isCatCollapsed}
                            >
                              <span className="tasks-group__title">{cat.category}</span>
                              <span className="tasks-group__count">{cat.count}</span>
                              <ChevronDown
                                size={16}
                                className={`tasks-group__chevron ${
                                  isCatCollapsed ? '' : 'is-expanded'
                                }`}
                              />
                            </button>
                          )}
                          <Collapsible open={!isCatCollapsed}>
                            <div className="tasks-group__items">
                              {cat.tasks.map((task) => (
                                <EmployeeTaskRow
                                  key={task.id}
                                  task={task}
                                  onOpen={openTaskDetails}
                                />
                              ))}
                            </div>
                          </Collapsible>
                        </div>
                      );
                    })}
                  </div>
                </Collapsible>
              </div>
            );
          })}
        </div>
      )}

      {/* Right Side Panel - Detalji zadatka (read-only pregled) */}
      <div className={`tasks-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
        <div
          className="side-panel-header"
          style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: '500',
              color: 'var(--cds-text-primary)',
            }}
          >
            Detalji zadatka
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={closePanel}
          />
        </div>

        <div className="side-panel-content">
          {selectedTask && (
            <div className="task-details">
              <div className="task-details__header">
                <div className="task-details__icon">
                  <Task size={28} />
                </div>
                <h2 className="task-details__title">{selectedTask.title}</h2>
              </div>

              <div className="task-details__tags">
                <Tag type="blue" size="sm">
                  {selectedTask.activityTypeName || selectedTask.activityType || 'Zadatak'}
                </Tag>
                {selectedTask.assignToAll && (
                  <Tag type="outline" size="sm">
                    Dodeljeno svima
                  </Tag>
                )}
                <span
                  className={`tasks-priority tasks-priority--${severityClass(
                    selectedTask.severity
                  )}`}
                >
                  {severityLabel(selectedTask.severity)}
                </span>
                <span className={`tasks-status tasks-status--${selectedTask.status}`}>
                  {STATUS_LABELS[selectedTask.status] || selectedTask.status}
                </span>
              </div>

              {selectedTask.description && (
                <div className="task-details__section">
                  <h4 className="task-details__label">Opis</h4>
                  <p className="task-details__value">{selectedTask.description}</p>
                </div>
              )}

              <div className="task-details__section">
                <h4 className="task-details__label">Informacije</h4>
                <div className="task-details__info">
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Korisnik</span>
                    <span className="task-details__info-value">
                      {selectedTask.residentName || '-'}
                    </span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Tip aktivnosti</span>
                    <span className="task-details__info-value">
                      {selectedTask.activityTypeName || selectedTask.activityType || '-'}
                    </span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Zakazano vreme</span>
                    <span className="task-details__info-value">
                      {selectedTask.scheduledTime
                        ? `${formatDate(selectedTask.scheduledTime)} ${formatTime(
                            selectedTask.scheduledTime
                          )}`
                        : '-'}
                    </span>
                  </div>

                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Zadužena osoba</span>
                    <span className="task-details__info-value">
                      {selectedTask.assignToAll ? (
                        'Svi zaposleni'
                      ) : selectedTask.assignedToId ? (
                        selectedTask.assignedToName ? (
                          <Link
                            to={`/employees/${selectedTask.assignedToId}`}
                            className="task-details__employee-link"
                          >
                            {selectedTask.assignedToName}
                          </Link>
                        ) : (
                          '-'
                        )
                      ) : (
                        '-'
                      )}
                    </span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Prati</span>
                    <span className="task-details__info-value">
                      {getTaskEmployeeTypeNames(selectedTask)}
                    </span>
                  </div>
                  <div className="task-details__info-row">
                    <span className="task-details__info-label">Status</span>
                    <span className="task-details__info-value">
                      {STATUS_LABELS[selectedTask.status] || selectedTask.status}
                    </span>
                  </div>
                  {selectedTask.executedAt && (
                    <div className="task-details__info-row">
                      <span className="task-details__info-label">Izvršeno</span>
                      <span className="task-details__info-value">
                        {formatDate(selectedTask.executedAt)}{' '}
                        {formatTime(selectedTask.executedAt)}
                      </span>
                    </div>
                  )}
                  {selectedTask.canceledAt && (
                    <div className="task-details__info-row">
                      <span className="task-details__info-label">Otkazano</span>
                      <span className="task-details__info-value">
                        {formatDate(selectedTask.canceledAt)}{' '}
                        {formatTime(selectedTask.canceledAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedTask.cancelNote && (
                <div className="task-details__section">
                  <h4 className="task-details__label">Razlog otkazivanja</h4>
                  <p className="task-details__value">{selectedTask.cancelNote}</p>
                </div>
              )}

              {/* Komentari vezani za zadatak (expand/collapse) */}
              <div className="task-details__section">
                <button
                  type="button"
                  className={`task-details__notes-toggle ${
                    isNotesExpanded ? 'is-expanded' : ''
                  }`}
                  onClick={() => setIsNotesExpanded((prev) => !prev)}
                  aria-expanded={isNotesExpanded}
                >
                  <span className="task-details__label">
                    Komentari{notes.length > 0 ? ` (${notes.length})` : ''}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`task-details__chevron ${isNotesExpanded ? 'is-expanded' : ''}`}
                  />
                </button>

                <Collapsible open={isNotesExpanded}>
                  {notesLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <SkeletonText width="70%" />
                      <SkeletonText width="50%" />
                    </div>
                  ) : notesError ? (
                    <p className="task-details__value">{notesError}</p>
                  ) : notes.length === 0 ? (
                    <p className="task-details__value">Nema komentara</p>
                  ) : (
                    <div className="task-details__notes">
                      {notes.map((note) => (
                        <div key={note.id} className="task-details__note">
                          <div className="task-details__note-header">
                            <span className="task-details__note-author">
                              {note.employeeName || 'Nepoznat korisnik'}
                            </span>
                            <span className="task-details__note-date">
                              {formatDate(note.createdAt)} {formatTime(note.createdAt)}
                            </span>
                          </div>
                          <p className="task-details__note-text">{note.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Collapsible>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay */}
      {isSidePanelOpen && <div className="side-panel-overlay" onClick={closePanel} />}
    </div>
  );
};

export default EmployeeTasksTab;
